import * as Sentry from '@sentry/react';
import type { Extras } from '@sentry/core';

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Error categories for better organization
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  NETWORK = 'network',
  STREAM_CHAT = 'stream_chat',
  VIDEO_CALL = 'video_call',
  PUSH_NOTIFICATIONS = 'push_notifications',
  PERMISSIONS = 'permissions',
  UI = 'ui',
  GENERAL = 'general',
}

// Type definitions for external libraries
interface AppInsights {
  trackEvent: (event: { name: string; properties: Record<string, unknown> }) => void;
  trackException: (event: { exception: Error; properties: Record<string, unknown> }) => void;
}

interface SentryScope {
  setUser: (user: { id: string }) => void;
  setTag: (key: string, value: string) => void;
  setLevel: (level: Sentry.SeverityLevel) => void;
  setExtras: Sentry.Scope['setExtras'];
  setExtra: Sentry.Scope['setExtra'];
}

declare global {
  interface Window {
    appInsights?: AppInsights;
  }
}

interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

interface ErrorLogEntry {
  error: unknown;
  eventName: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  context?: ErrorContext;
  extras?: Extras;
}

// Core error serialization utilities
export const getErrorString = (error: unknown): string => {
  if (!error) return '';

  let errorString = '';
  if (typeof error === 'string') {
    errorString = error;
  } else if (
    typeof error === 'object' &&
    'error' in error &&
    typeof (error as Record<string, unknown>).error === 'string'
  ) {
    errorString = (error as Record<string, unknown>).error as string;
  } else if (error instanceof Error) {
    errorString = `${error.name}: ${error.message}`;
  } else {
    errorString = error.toString();
  }

  return errorString;
};

export const safeSerializeError = (error: unknown): string => {
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return getErrorString(error);
  }
};

// Simplified Error Logger Class for Web
class UnifiedErrorLogger {
  private isEnabled: boolean = true;

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  log(eventName: string, error: unknown, context?: string | ErrorContext, extras?: Extras): void {
    if (!this.isEnabled) return;

    try {
      const normalizedContext =
        typeof context === 'string' ? { component: context } : context || {};

      const severity = this.detectSeverity(eventName);
      const category = this.detectCategory(eventName);

      if (import.meta.env.DEV) {
        console.error(`[${eventName}]:`, getErrorString(error), normalizedContext);
      }

      this.reportToServices({
        error,
        eventName,
        severity,
        category,
        context: normalizedContext,
        extras,
      });
    } catch {
      console.error(`Failed to log error for ${eventName}:`, getErrorString(error));
    }
  }

  critical(eventName: string, error: unknown, context?: string | ErrorContext): void {
    this.log(`critical-${eventName}`, error, context);
  }

  event(eventName: string, data: Record<string, unknown>, message?: string): void {
    if (!this.isEnabled && !import.meta.env.DEV) return;

    try {
      const eventData: Record<string, unknown> = {
        ...data,
        timestamp: Date.now(),
        platform: 'web',
      };

      if (data.error) {
        eventData.error = getErrorString(data.error);
      }

      if (import.meta.env.DEV) {
        const logMessage = message || `[EVENT] ${eventName}`;
        console.log(logMessage, eventData);
      }

      if (this.isEnabled) {
        if (typeof window !== 'undefined' && window.appInsights) {
          window.appInsights.trackEvent({
            name: eventName,
            properties: eventData,
          });
        }
      }
    } catch {
      console.error(`Failed to log event ${eventName}:`, data);
    }
  }

  info(message: string, eventName: string, data?: Record<string, unknown>): void {
    this.event(eventName, data || {}, message);
  }

  private detectSeverity(eventName: string): ErrorSeverity {
    const name = eventName.toLowerCase();
    if (
      name.indexOf('critical') !== -1 ||
      name.indexOf('fatal') !== -1 ||
      name.indexOf('crash') !== -1
    ) {
      return ErrorSeverity.CRITICAL;
    }
    if (
      name.indexOf('init') !== -1 ||
      name.indexOf('auth') !== -1 ||
      name.indexOf('payment') !== -1
    ) {
      return ErrorSeverity.HIGH;
    }
    if (
      name.indexOf('exception') !== -1 ||
      name.indexOf('error') !== -1 ||
      name.indexOf('failed') !== -1
    ) {
      return ErrorSeverity.MEDIUM;
    }
    return ErrorSeverity.LOW;
  }

  private detectCategory(eventName: string): ErrorCategory {
    const name = eventName.toLowerCase();
    if (
      name.indexOf('auth') !== -1 ||
      name.indexOf('login') !== -1 ||
      name.indexOf('token') !== -1
    ) {
      return ErrorCategory.AUTHENTICATION;
    }
    if (name.indexOf('stream') !== -1 || name.indexOf('chat') !== -1) {
      return ErrorCategory.STREAM_CHAT;
    }
    if (name.indexOf('video') !== -1 || name.indexOf('call') !== -1) {
      return ErrorCategory.VIDEO_CALL;
    }
    if (
      name.indexOf('push') !== -1 ||
      name.indexOf('notification') !== -1 ||
      name.indexOf('permission') !== -1
    ) {
      return ErrorCategory.PUSH_NOTIFICATIONS;
    }
    if (
      name.indexOf('network') !== -1 ||
      name.indexOf('api') !== -1 ||
      name.indexOf('axios') !== -1
    ) {
      return ErrorCategory.NETWORK;
    }
    return ErrorCategory.GENERAL;
  }

  private reportToServices(errorEntry: ErrorLogEntry): void {
    const { error, severity, context, eventName, extras } = errorEntry;

    try {
      if (error instanceof Error || typeof error === 'object') {
        if (Sentry?.captureException) {
          Sentry.withScope((scope: SentryScope) => {
            if (context?.userId) {
              scope.setUser({ id: context.userId });
            }
            if (context?.component) {
              scope.setTag('component', context.component);
            }
            if (context?.action) {
              scope.setTag('action', context.action);
            }
            scope.setLevel(this.getSentryLevel(severity));

            if (extras?.status) {
              scope.setTag('error-status', extras.status as string);
            }
            if (extras?.errorBody) {
              scope.setExtra('errorBody', extras.errorBody as string);
            }
            if (extras?.url) {
              scope.setTag('target-url', extras.url as string);
            }
            if (extras?.queryKey) {
              scope.setTag('queryKey', JSON.stringify(extras.queryKey));
            }
            if (extras?.queryMeta) {
              scope.setExtra('queryMeta', JSON.stringify(extras.queryMeta));
            }
            if (extras?.key) {
              scope.setExtra('key', extras.key as string);
            }

            Sentry.captureException(error);
          });
        }
      }

      if (typeof window !== 'undefined' && window.appInsights) {
        const properties = {
          error: getErrorString(error),
          severity,
          category: errorEntry.category,
          timestamp: Date.now().toString(),
          platform: 'web',
          ...context,
        };

        window.appInsights.trackEvent({
          name: eventName,
          properties,
        });

        if (severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH) {
          window.appInsights.trackException({
            exception: error instanceof Error ? error : new Error(getErrorString(error)),
            properties,
          });
        }
      }
    } catch (reportingError) {
      console.error('Failed to report to services:', getErrorString(reportingError));
    }
  }

  private getSentryLevel(severity: ErrorSeverity): Sentry.SeverityLevel {
    switch (severity) {
      case ErrorSeverity.LOW:
        return 'info';
      case ErrorSeverity.MEDIUM:
        return 'warning';
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.CRITICAL:
        return 'fatal';
      default:
        return 'error';
    }
  }
}

// Export singleton instance
export const ErrorLogger = new UnifiedErrorLogger();

// Convenient shortcuts
export const logError = (eventName: string, error: unknown, context?: string, extras?: Extras) => {
  if (import.meta.env.DEV) {
    console.error('logError', { eventName, error, context, extras });
  }
  ErrorLogger.log(eventName, error, context, extras);
};

export const logCritical = (eventName: string, error: unknown, context?: string) => {
  if (import.meta.env.DEV) {
    console.error('logCritical', { eventName, error, context });
  }
  ErrorLogger.critical(eventName, error, context);
};

export const logInfo = (message: string, eventName: string, data?: Record<string, unknown>) => {
  if (import.meta.env.DEV) {
    console.info('logInfo', { message, eventName, data });
  }
  ErrorLogger.info(message, eventName, data);
};

// Legacy exports for backward compatibility
export const reportError = (error: unknown): void => {
  ErrorLogger.log('legacy-error', error);
};
